import { normalizeTimeZone } from '@mangovoice/constants'

import type { TimezoneInfo, WakeupCall } from '../types/wakeup'
import { extensionUuidKey } from './extensionUuid'
import {
  findUtcInstantForZonedWallClock,
  normalizeWakeTimeHms,
} from './wakeupTimezone'

/**
 * Map of extension_uuid → IANA timezone identifier.
 * Built from extension-lines data with voicemail_time_zone normalized.
 */
export type ExtensionTimezoneMap = Record<string, string>

/**
 * Resolve the effective display timezone for a wakeup call record.
 * Priority: extension timezone (per-room) → PBX timezone (system default).
 */
export const resolveWakeupDisplayTimeZone = (
  wc: WakeupCall,
  extensionTzMap: ExtensionTimezoneMap,
  pbxTimeZone: string,
): string => {
  const extensionTz = extensionTzMap[extensionUuidKey(wc.extension_uuid)]
  return extensionTz ?? pbxTimeZone
}

/**
 * Build an ExtensionTimezoneMap from extension-lines data.
 * Normalizes Rails-style timezone labels to IANA identifiers.
 */
export const buildExtensionTimezoneMap = (
  extensions: Array<{
    extension_uuid: string
    voicemail_time_zone: string | null
  }>,
  pbxTimeZone: string,
): ExtensionTimezoneMap => {
  const map: ExtensionTimezoneMap = {}
  for (const ext of extensions) {
    const key = extensionUuidKey(ext.extension_uuid)
    if (ext.voicemail_time_zone) {
      map[key] = normalizeTimeZone(ext.voicemail_time_zone)
    } else {
      map[key] = pbxTimeZone
    }
  }
  return map
}

/**
 * Absolute instant for a wake-up row (prefer canonical UTC from the API).
 */
export const getWakeupInstantIso = (wc: WakeupCall): string | null => {
  const w = wc.wakeup_timestamp
  if (w?.iso_8601_utc) return w.iso_8601_utc
  if (w?.timestamp) return w.timestamp
  if (wc.wuc_timestamp) return wc.wuc_timestamp
  return null
}

/**
 * Compact schedule line: "6:30 am - Tue 4/28" (en) or "6:30 am - mar. 28/4" (fr).
 * Time portion uses a fixed layout; date portion is locale-ordered.
 */
export const formatInstantScheduleStyle = (
  instant: Date,
  locale: string,
  displayTimeZone: string,
): string => {
  const timeFmt = new Intl.DateTimeFormat(locale, {
    timeZone: displayTimeZone,
    hour: 'numeric',
    minute: '2-digit',
  })
  const dateFmt = new Intl.DateTimeFormat(locale, {
    timeZone: displayTimeZone,
    weekday: 'short',
    month: 'numeric',
    day: 'numeric',
  })
  return `${timeFmt.format(instant)} - ${dateFmt.format(instant)}`
}

/**
 * Format a wake-up call's timestamp for display in the given timezone.
 * The caller is responsible for resolving the correct timezone
 * (extension timezone with PBX timezone as fallback).
 *
 * @returns e.g. "Apr 28, 2026, 6:30 AM EDT"
 */
export const formatWakeupTime = (
  tz: TimezoneInfo | null | undefined,
  locale: string,
  displayTimeZone: string,
): string => {
  if (!tz) return '—'
  const iso = tz.iso_8601_utc ?? tz.timestamp
  if (!iso) return tz.iso_8601_local ?? '—'
  const instant = new Date(iso)
  if (Number.isNaN(instant.getTime())) {
    return tz.iso_8601_local ?? '—'
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: displayTimeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(instant)
  } catch {
    return tz.iso_8601_local ?? '—'
  }
}

/**
 * Format a plain ISO 8601 string in a given timezone.
 * Used for fields like `created_at` that aren't wrapped in a TimezoneInfo object.
 *
 * @returns e.g. "Apr 28, 2026, 6:30 AM EDT"
 */
export const formatIsoInTimeZone = (
  isoString: string | null | undefined,
  locale: string,
  displayTimeZone: string,
): string => {
  if (!isoString) return '—'
  const instant = new Date(isoString)
  if (Number.isNaN(instant.getTime())) return isoString

  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: displayTimeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(instant)
  } catch {
    return isoString
  }
}

/**
 * Format an ISO 8601 timestamp for call history display.
 *
 * @returns Formatted string like "Jan 15, 2025 10:30 AM"
 */
export const formatCallTime = (isoString: string): string => {
  try {
    const date = new Date(isoString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return isoString
  }
}

/**
 * Format an ISO 8601 timestamp as a relative time string.
 * Used for "last updated" style displays.
 *
 * @returns Relative string like "2 hours ago", "just now", "3 days ago"
 */
export const formatRelativeTime = (isoString: string): string => {
  try {
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffSeconds < 60) return 'just now'
    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    }
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    }
    if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    }
    return formatCallTime(isoString)
  } catch {
    return isoString
  }
}

/**
 * Format a voicemail received time like "9:46 AM - 12/30".
 * Accepts either ISO strings or epoch seconds.
 */
export const formatVoicemailTime = (
  value: string | number | null | undefined,
): string => {
  if (value === null || value === undefined) return '—'
  try {
    const date =
      typeof value === 'number'
        ? new Date(value > 1_000_000_000_000 ? value : value * 1000)
        : new Date(value)

    if (Number.isNaN(date.getTime())) {
      return String(value)
    }

    const time = date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    const dateStr = date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
    })
    return `${time} - ${dateStr}`
  } catch {
    return String(value)
  }
}

/**
 * Format a wakeup call schedule time like "6:00 am - Fri 3/06" in the PBX TZ.
 */
export const formatScheduleTime = (
  wc: WakeupCall,
  locale: string,
  displayTimeZone: string,
): string => {
  const iso = getWakeupInstantIso(wc)
  if (!iso) return wc.wuc_timestamp
  const instant = new Date(iso)
  if (Number.isNaN(instant.getTime())) {
    return wc.wuc_timestamp
  }
  try {
    return formatInstantScheduleStyle(instant, locale, displayTimeZone)
  } catch {
    return wc.wuc_timestamp
  }
}

/**
 * Format a pending wakeup change (PBX-local date + time) for display in PBX TZ.
 */
export const formatPendingTime = (
  dateStr: string,
  time: string,
  locale: string,
  displayTimeZone: string,
): string => {
  const timeNorm = normalizeWakeTimeHms(time)
  const instant = findUtcInstantForZonedWallClock(
    dateStr,
    timeNorm,
    displayTimeZone,
  )
  if (!instant || Number.isNaN(instant.getTime())) {
    // DST-gap fallback: the requested wall-clock time doesn't exist in the
    // display TZ, so we render the time in UTC (where it's always valid)
    // and the date in the display TZ (which is still correct for the day).
    const [h, m] = time.split(':')
    const hour = parseInt(h, 10)
    const fakeInstant = new Date(
      `${dateStr}T${String(hour).padStart(2, '0')}:${m}:00Z`,
    )
    const timeFmt = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
    })
    const dateFmt = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      timeZone: displayTimeZone,
    })
    const d = new Date(`${dateStr}T12:00:00Z`)
    return `${timeFmt.format(fakeInstant)} - ${dateFmt.format(d)}`
  }
  return formatInstantScheduleStyle(instant, locale, displayTimeZone)
}

/**
 * Format a duration in seconds to a human-readable string.
 *
 * @returns Formatted string like "2m 30s" or "1h 5m"
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}
